import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../config/api";

const TICKET_COUNT_ENDPOINT = `${API_BASE_URL}/api/v0/notifications/admin/counts/`;
const REFRESH_ENDPOINT = `${API_BASE_URL}/api/v0/auth/admin/jwt/refresh/`;

const TicketCountContext = createContext(null);

const hasAuthStorage = () =>
  Boolean(localStorage.getItem("access") || localStorage.getItem("refresh"));

export function TicketCountProvider({ children }) {
  const navigate = useNavigate();
  const [ticketCount, setTicketCount] = useState(0);

  const logout = useCallback(() => {
    localStorage.removeItem("access");
    localStorage.removeItem("authCredentials");
    localStorage.removeItem("refresh");
    localStorage.removeItem("role");
    localStorage.removeItem("email");
    setTicketCount(0);
    navigate("/signin", { replace: true });
  }, [navigate]);

  const authFetch = useCallback(
    async (url, options = {}) => {
      const headers = new Headers(options.headers || {});
      headers.set("Authorization", `Bearer ${localStorage.getItem("access")}`);

      let response = await fetch(url, {
        ...options,
        headers,
      });

      if (response.status !== 401) return response;

      const refreshResponse = await fetch(REFRESH_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          refresh: localStorage.getItem("refresh"),
        }),
      });

      if (!refreshResponse.ok) {
        logout();
        return null;
      }

      const refreshData = await refreshResponse.json();
      localStorage.setItem("access", refreshData.access);

      const retryHeaders = new Headers(options.headers || {});
      retryHeaders.set(
        "Authorization",
        `Bearer ${localStorage.getItem("access")}`,
      );

      return fetch(url, {
        ...options,
        headers: retryHeaders,
      });
    },
    [logout],
  );

  const fetchTicketCount = useCallback(async () => {
    if (!hasAuthStorage()) {
      setTicketCount(0);
      return;
    }

    try {
      const response = await authFetch(TICKET_COUNT_ENDPOINT, {
        method: "GET",
      });

      if (!response) return;
      if (!response.ok) {
        throw new Error("load-ticket-count-failed");
      }

      const payload = await response.json();
      const nextTicketCount = Number(payload?.unreach_ticket_count ?? 0);
      setTicketCount(Number.isFinite(nextTicketCount) ? nextTicketCount : 0);
    } catch (error) {
      console.error("Error fetching ticket count:", error);
    }
  }, [authFetch]);

  useEffect(() => {
    if (!hasAuthStorage()) return;
    fetchTicketCount();
  }, [fetchTicketCount]);

  const value = useMemo(
    () => ({
      ticketCount,
      haveNotifications: ticketCount > 0,
      fetchTicketCount,
    }),
    [fetchTicketCount, ticketCount],
  );

  return (
    <TicketCountContext.Provider value={value}>
      {children}
    </TicketCountContext.Provider>
  );
}

export function useTicketCount() {
  const context = useContext(TicketCountContext);
  if (!context) {
    throw new Error("useTicketCount must be used within TicketCountProvider");
  }

  return context;
}
